import React, { useEffect, useRef, useState } from "react";
import Toolbox from "@components/Toolbox";
import SettingsPanel from "@components/SettingsPanel";
import Topbar from "@components/Topbar";

// editor frame components
import CustomHero from "@components/hero/CustomHero";
import FeaturedCarousel from "@components/carousel/featured/FeaturedCarousel";
import CategoryCarousel from "@components/carousel/category/CategoryCarousel";
import ContainerBuilderComponent from "@components/user/Container";
import BasicCarouselBuilderComponent from "@components/carousel/basic/BasicCarouselBuilderComponent";
import EventHeroBuilderComponent from "@components/carousel/hero/EventHero";
import BasicBannerBuilderComponent from "@components/banner/BasicBanner";
import PortraitCarousel from "@components/carousel/portrait/PortraitCarousel";
import PersistentCarousel from "@components/carousel/persistent/PersistentCarousel";

// editor layout components
import Panel from "@components/editor/Panel";
import RenderPanelSettingsControls from "@components/editor/RenderPanelSettingsControls";

import { Editor, Frame, Element } from "@craftjs/core";

import { AppType, appTypes } from "@utils/constants";
import { apiClient } from "@api/index";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "@state/auth";
import { useThemeStore } from "@state/theme";
import { RenderNode } from "@components/OnRenderWrapper";
import { Autocomplete, TextField, Tooltip, IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useEditorStore } from "@state/editor";
import { useConfigStore } from "@state/config";
import { hasPermission, hasAnyPermission } from "@utils/permissions";

type ComponentType = React.FC<any>;

const components: Record<string, ComponentType> = {
  Container: ContainerBuilderComponent,
  CustomHero,
  EventHero: EventHeroBuilderComponent,
  FeaturedCarousel,
  CategoryCarousel,
  BasicCarousel: BasicCarouselBuilderComponent,
  BasicBanner: BasicBannerBuilderComponent,
  PortraitCarousel,
  PersistentCarousel,
};

const getComponents = (data: any) => {
  for (const nodeId in data) {
    const element = data[nodeId];

    // If the element is the root node
    if (nodeId === "ROOT") {
      return (
        <Element canvas is={ContainerBuilderComponent} {...element.props}>
          {element.nodes.map((childId: string) => {
            const node = data[childId];
            if (!components[node?.type?.resolvedName]) return null;

            return (
              <Element
                {...node.props}
                key={childId}
                id={childId}
                is={components && components[node?.type?.resolvedName]}
              />
            );
          })}
        </Element>
      );
    } else {
      // If you have nested nodes, handle them similarly, omitted here for brevity.
    }
  }
};

const EditorLayout = () => {
  const params = useParams();
  const { appID } = params;
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);
  const { setTheme } = useThemeStore();
  const { setConfig } = useConfigStore();
  const { setIsPreview, setRenderPanelSize, isPreview } = useEditorStore();

  // Check permissions
  const canRead = hasPermission(user.roles, "manage_ott", "read");
  const canUpdate = hasPermission(user.roles, "manage_ott", "update");

  const renderPanelRef = useRef<HTMLDivElement | null>(null);
  const [componentsPanelOpen, setComponentsPanelOpen] = useState(true);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(true);

  const [isComponentsPanelOpen, setIsComponentsPanelOpen] = useState(true);
  const [appName, setAppName] = useState<string>("");
  const [formAppTypes, setFormAppTypes] = useState<{ title: string; value: string }[]>([]);

  // Fetch app data
  const { data, isLoading } = apiClient.ott.getAppByID.useQuery(
    {
      appID: appID as string,
      clientID: user.clientID,
    },
    {
      refetchOnWindowFocus: false,
      onSuccess(res) {
        const { manifest, appName, targets } = res;
        const { data } = manifest;
        const { theme, config } = data;
        console.log(config);
        console.log("Manifest", manifest);

        setTheme(theme);
        if (config) {
          setConfig(config);
        }
        setAppName(appName);

        setFormAppTypes(
          targets.map((target: string) => {
            let title = "";
            let value = "";

            switch (target) {
              case "SamsungTV":
                title = "Samsung TV";
                value = appTypes.samsungTv;
                break;
              case "AppleTV":
                title = "Apple TV";
                value = appTypes.appleTv;
                break;
              case "AndroidTV":
                title = "Android TV";
                value = appTypes.androidTv;
                break;
              case "FireTV":
                title = "Fire TV";
                value = appTypes.fireTv;
                break;
              default:
                break;
            }

            return { title, value };
          })
        );
      },
    }
  );

  const toggleComponentsPanelOpen = () => {
    setComponentsPanelOpen(!componentsPanelOpen);
  };

  const toggleSettingsPanelOpen = () => {
    setSettingsPanelOpen(!settingsPanelOpen);
  };

  // Make sure we aren’t in preview mode
  useEffect(() => {
    if (!isPreview) {
      setIsPreview(false);
    }
  }, [isPreview, setIsPreview]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip the prompt when you’re already in preview mode, if desired
      if (isPreview) return;

      e.preventDefault();      // Firefox
      e.returnValue = "";      // Chrome / Edge
      //  (You can’t use window.alert/confirm here – the spec disallows it;
      //   the browser shows its own, standard confirm-close dialog.)
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isPreview]);

  // Resize observer for the editor content
  useEffect(() => {
    const renderPanel = renderPanelRef.current;
    if (renderPanel) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect) {
            const { width, height } = entry.contentRect;
            requestAnimationFrame(() => {
              setRenderPanelSize({ width, height });
            });
          }
        }
      });

      resizeObserver.observe(renderPanel);
      return () => resizeObserver.disconnect();
    }
  }, [renderPanelRef, setRenderPanelSize]);

  return (
    <div className="flex flex-col h-screen">
      <Editor
        indicator={{ thickness: 10, success: "#00ff00" }}
        resolver={components}
        enabled={!isPreview && canUpdate}
        onRender={(render) => (
          <RenderNode
            render={render.render}
            setIsComponentsPanelOpen={setIsComponentsPanelOpen}
          />
        )}
      >
        {/* Top bar can keep its full-height styling internally if desired */}
        <Topbar
          appID={appID as string}
          appStatus={data?.manifest?.data?.status}
          appName={appName}
          appTypes={formAppTypes.map((item) => item.value as AppType)}
          toggleComponentsPanelOpen={toggleComponentsPanelOpen}
          toggleSettingsPanelOpen={toggleSettingsPanelOpen}
        />

        {/* 
          The key is this row is `h-full overflow-auto` so the entire editor 
          area (below the top bar) can scroll while still only taking up 
          the screen’s total height. 
        */}
        <div className="flex flex-row h-full overflow-auto">
          {canUpdate && (
            <Panel
              title="Components"
              tailwindBorderStyles="border-r border-gray-300"
              open={componentsPanelOpen && !isPreview}
            >
              <Toolbox />
            </Panel>
          )}

          <div className="flex flex-col flex-1 min-w-0 overflow-auto">
            {/* If you have extra controls, you can keep them:
            {!isPreview && <RenderPanelSettingsControls renderPanelRef={renderPanelRef} />} */}
            <div className="flex justify-center w-full">
              <div ref={renderPanelRef} id="preview-container" className="w-full max-w-[1080px]">
                {data?.manifest.data.elements && (
                  <Frame>{getComponents(data?.manifest.data.elements)}</Frame>
                )}
              </div>
            </div>
          </div>

          {canUpdate && (
            <Panel
              multiPanel
              title="Component Settings"
              secondTitle="General Settings"
              tailwindBorderStyles="border-l border-gray-300"
              open={settingsPanelOpen && !isPreview}
              secondPanelChildren={<ThemeSettingsPanel />}
              isFirstPanelOpen={isComponentsPanelOpen}
              setIsFirstPanelOpen={setIsComponentsPanelOpen}
            >
              <SettingsPanel />
            </Panel>
          )}
        </div>
      </Editor>
    </div>
  );
};

export default EditorLayout;

const ThemeSettingsPanel = () => {
  const theme = useThemeStore();
  const setThemeValue = useThemeStore(({ setThemeValue }) => setThemeValue);
  const { allowVAST, thumbMissingUrl, setThumbMissingUrl, setAllowVAST } = useConfigStore();

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setThemeValue(name, value);
  };

  const handleAllowVASTChange = (allow: boolean) => {
    setAllowVAST(allow);
  };

  const handleThumbMissingUrlChange = (url: string) => {
    setThumbMissingUrl(url);
  };

  return (
    <div className="flex flex-col gap-6 px-3 mt-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Configuration</h2>

        <div className="flex flex-col gap-6">
          <Autocomplete
            id="allowVAST"
            size="small"
            options={[true, false]} // Only true/false
            getOptionLabel={(option) => (option ? "Yes" : "No")}
            onChange={(_, newValue) => {
              if (newValue === null) return;
              handleAllowVASTChange(newValue);
            }}
            defaultValue={allowVAST}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span style={{ display: "flex", alignItems: "center" }}>
                    Allow VAST
                    <Tooltip
                      title="Enabling this option will display the ad campaigns you have set up in Control Center."
                      placement="right"
                    >
                      <IconButton size="small" aria-label="Help on Allow VAST" style={{ marginLeft: 4 }}>
                        <HelpOutlineIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </span>
                }
                placeholder="Select an option"
              />
            )}
          />

          <TextField
            fullWidth
            size="small"
            // label="Default Thumbnail Url"
            label={
              <span style={{ display: "flex", alignItems: "center" }}>
                Default Thumbnail Url
                <Tooltip
                  title="This thumbnail shows when an event is Live or missing its thumbnail."
                  placement="right"
                >
                  <IconButton size="small" aria-label="Help on Default Thumbnail" style={{ marginLeft: 4 }}>
                    <HelpOutlineIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </span>
            }
            required
            value={thumbMissingUrl}
            onChange={(e) => handleThumbMissingUrlChange(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Theme</h2>

        {/* <TextField
          id="text-color"
          name="textColor"
          label="Text Color"
          type="color"
          variant="outlined"
          placeholder="Text Color"
          value={theme.textColor}
          onChange={handleThemeChange}
          required
        /> */}

        <TextField
          id="background-color"
          name="backgroundColor"
          label="Background Color"
          type="color"
          variant="outlined"
          placeholder="Background Color"
          value={theme.backgroundColor}
          onChange={handleThemeChange}
          required
        />

        <TextField
          id="primary-button-color"
          name="primaryColor"
          label="Button Color"
          type="color"
          variant="outlined"
          placeholder="Primary Button Color"
          value={theme.primaryColor}
          onChange={handleThemeChange}
          required
        />

        {/* <TextField
          id="secondary-button-color"
          name="secondaryColor"
          label="Secondary Button Color"
          type="color"
          variant="outlined"
          placeholder="Secondary Button Color"
          value={theme.secondaryColor}
          onChange={handleThemeChange}
          required
        /> */}

        <TextField
          id="accent-color"
          name="accentColor"
          label="Accent Color"
          type="color"
          variant="outlined"
          placeholder="Accent Color"
          value={theme.accentColor}
          onChange={handleThemeChange}
          required
        />

        <TextField
          id="focus-ring-color"
          name="focusRingColor"
          label="Focus Ring Color"
          type="color"
          variant="outlined"
          placeholder="Focus Ring Color"
          value={theme.focusRingColor}
          onChange={handleThemeChange}
          required
        />
      </div>
    </div>
  );
};
